import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Container, Row, Col, Card, Form, Button, Spinner, Alert } from 'react-bootstrap';
import { FaSave, FaArrowLeft, FaTimes } from 'react-icons/fa';
import PageHeader from '../components/PageHeader';
import productService from '../services/productService';
import categoryService from '../services/categoryService';
import vendorService from '../services/vendorService';
import apiClient from '../services/apiClient';

function ProductEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    category_id: '',
    vendor_id: '',
    image: '',
    file_path: '',
    file_type: '',
    files_included: '',
    compatible_with: '',
    is_published: true,
    media: {}
  });

  const STORAGE_URL = process.env.REACT_APP_STORAGE_URL || 'http://localhost:5000/static/';

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
      return imagePath;
    }
    return `${STORAGE_URL}${imagePath}`;
  };

  const fetchProduct = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const data = await productService.getById(id);
      
      // Parse media if it's a JSON string
      let parsedMedia = {};
      
      if (data.media) {
        parsedMedia = typeof data.media === 'string' ? JSON.parse(data.media) : data.media;
      }
      
      setFormData({
        title: data.title || '',
        description: data.description || '',
        price: data.price || '',
        category_id: data.category_id || '',
        vendor_id: data.vendor_id || '',
        image: data.image || '',
        file_path: data.file_path || '',
        file_type: data.file_type || '',
        files_included: data.files_included || '',
        compatible_with: data.compatible_with || '',
        is_published: data.is_published !== undefined ? data.is_published : true,
        media: parsedMedia
      });
    } catch (err) {
      setError('Failed to load product');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchCategories = useCallback(async () => {
    try {
      const data = await categoryService.getAll({ paginate: 'false' });
      setCategories(Array.isArray(data) ? data : data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const data = await vendorService.getActive();
      setVendors(Array.isArray(data) ? data : data.vendors || []);
    } catch (err) {
      console.error('Failed to load vendors:', err);
    }
  }, []);

  useEffect(() => {
    fetchProduct();
    fetchCategories();
    fetchVendors();
  }, [fetchProduct, fetchCategories, fetchVendors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setError('');

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const resp = await apiClient.post('/upload_image', formDataUpload);
      const data = resp.data;
      if (resp.status === 201) {
        setFormData({ ...formData, image: `products/${data.filename}` });
      } else {
        setError('Failed to upload image');
      }
    } catch (err) {
      setError('Failed to upload image');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    setError('');

    try {
      const uploadedPaths = [];
      
      for (const file of files) {
        const formDataUpload = new FormData();
        formDataUpload.append('image', file);

        const resp = await apiClient.post('/upload_image', formDataUpload);
        const data = resp.data;
        if (resp.status === 201) {
          uploadedPaths.push(`products/${data.filename}`);
        }
      }

      if (uploadedPaths.length > 0) {
        const currentImages = formData.media?.images || [];
        setFormData({
          ...formData,
          media: {
            ...formData.media,
            images: [...currentImages, ...uploadedPaths]
          }
        });
      }
    } catch (err) {
      setError('Failed to upload media images');
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = ''; // Reset file input
    }
  };

  const handleRemoveMediaImage = (index) => {
    const currentImages = formData.media?.images || [];
    const newImages = currentImages.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      media: {
        ...formData.media,
        images: newImages
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      // Ensure media is a proper object
      const mediaObj = formData.media || {};
      
      const submitData = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        media: mediaObj
      };

      // submitData logged during development; removed to satisfy eslint no-console rule

      if (id) {
        await productService.update(id, submitData);
      } else {
        await productService.create(submitData);
      }
      navigate('/products');
    } catch (err) {
      setError(`Failed to ${id ? 'update' : 'create'} product: ${err.response?.data?.error || err.message}`);
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <Container fluid className="px-4">
      <PageHeader
        title={id ? 'Edit Product' : 'Add New Product'}
        subtitle={id ? `Editing product #${id}` : 'Create a new product'}
      />

      <Row className="mb-3">
        <Col>
          <Button variant="secondary" onClick={() => navigate('/products')}>
            <FaArrowLeft className="me-2" />
            Back to Products
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Form onSubmit={handleSubmit}>
        <Row className="g-4">
          <Col lg={8}>
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Basic Information</h5>
                
                <Form.Group className="mb-3">
                  <Form.Label>Product Title *</Form.Label>
                  <Form.Control
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                    placeholder="Enter product title"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Enter product description"
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Price *</Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        required
                        placeholder="0.00"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Category *</Form.Label>
                      <Form.Select
                        name="category_id"
                        value={formData.category_id}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Vendor</Form.Label>
                  <Form.Select
                    name="vendor_id"
                    value={formData.vendor_id}
                    onChange={handleChange}
                  >
                    <option value="">No Vendor</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                        {vendor.is_verified && ' ✓'}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Associate this product with a verified vendor/company
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mb-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Digital Product Details</h5>
                
                <Form.Group className="mb-3">
                  <Form.Label>File Path</Form.Label>
                  <Form.Control
                    type="text"
                    name="file_path"
                    value={formData.file_path}
                    onChange={handleChange}
                    placeholder="Path to downloadable file (e.g., products/file.zip)"
                  />
                  <Form.Text className="text-muted">
                    Relative path from storage directory
                  </Form.Text>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>File Type</Form.Label>
                      <Form.Control
                        type="text"
                        name="file_type"
                        value={formData.file_type}
                        onChange={handleChange}
                        placeholder="e.g., zip, pdf, psd"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Files Included</Form.Label>
                      <Form.Control
                        type="text"
                        name="files_included"
                        value={formData.files_included}
                        onChange={handleChange}
                        placeholder="e.g., .jpg, .png, .ai"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>Compatible With</Form.Label>
                  <Form.Control
                    type="text"
                    name="compatible_with"
                    value={formData.compatible_with}
                    onChange={handleChange}
                    placeholder="e.g., Photoshop, Illustrator, Figma"
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Check
                    type="switch"
                    id="is_published"
                    name="is_published"
                    label="Published (visible to customers)"
                    checked={formData.is_published}
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                  />
                  <Form.Text className="text-muted">
                    Uncheck to save as draft
                  </Form.Text>
                </Form.Group>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={4}>



            <Card className="border-0 shadow-sm">
              <Card.Body>
                <div className="d-grid gap-2">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaSave className="me-2" />
                        {id ? 'Update Product' : 'Create Product'}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/products')}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                </div>
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Product Image</h5>
                
                <Form.Group className="mb-3">
                  <Form.Label>Upload Image</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Upload a product image (JPG, PNG, GIF)
                  </Form.Text>
                </Form.Group>

                <div className="text-center text-muted mb-3">or</div>

                <Form.Group className="mb-3">
                  <Form.Label>Image URL</Form.Label>
                  <Form.Control
                    type="text"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder="Image path or URL"
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Enter relative path or full URL
                  </Form.Text>
                </Form.Group>

                {uploading && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <span className="ms-2 small text-muted">Uploading...</span>
                  </div>
                )}

                {formData.image && !uploading && (
                  <div className="mt-3">
                    <p className="small text-muted mb-2">Preview:</p>
                    <img
                      src={getImageUrl(formData.image)}
                      alt="Product preview"
                      className="img-fluid rounded"
                      style={{ maxHeight: '450px', objectFit: 'cover' }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </Card.Body>
            </Card>

            <Card className="border-0 shadow-sm mt-4">
              <Card.Body>
                <h5 className="fw-semibold mb-4">Media Gallery</h5>
                
                <Form.Group className="mb-3">
                  <Form.Label>Upload Multiple Images</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleMediaUpload}
                    disabled={uploading}
                  />
                  <Form.Text className="text-muted">
                    Upload multiple product images (JPG, PNG, GIF)
                  </Form.Text>
                </Form.Group>

                {uploading && (
                  <div className="text-center mb-3">
                    <Spinner animation="border" size="sm" variant="primary" />
                    <span className="ms-2 small text-muted">Uploading...</span>
                  </div>
                )}

                {formData.media?.images && formData.media.images.length > 0 && (
                  <div className="mt-3">
                    <p className="small text-muted mb-2">Gallery Images ({formData.media.images.length}):</p>
                    <Row className="g-2">
                      {formData.media.images.map((imagePath, index) => (
                        <Col xs={6} key={index}>
                          <div className="position-relative">
                            <img
                              src={getImageUrl(imagePath)}
                              alt={`Gallery ${index + 1}`}
                              className="img-fluid rounded"
                              style={{ width: '100%', height: '120px', objectFit: 'cover' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            <Button
                              variant="danger"
                              size="sm"
                              className="position-absolute top-0 end-0 m-1"
                              style={{ padding: '2px 6px' }}
                              onClick={() => handleRemoveMediaImage(index)}
                            >
                              <FaTimes size={12} />
                            </Button>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {(!formData.media?.images || formData.media.images.length === 0) && !uploading && (
                  <p className="text-muted text-center mb-0 small">No media images uploaded yet</p>
                )}
              </Card.Body>
            </Card>

          </Col>
        </Row>
      </Form>
    </Container>
  );
}

export default ProductEditPage;
